import { AnimatePresence, motion } from 'motion/react';
import { useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '~/ui-apps/design-system/organisms/Dialog';
import OnboardingProgress from './components/OnboardingProgress';
import OnboardingStepFrame from './components/OnboardingStepFrame';
import OnboardingCompletion from './components/OnboardingCompletion';
import OnboardingToast from './components/OnboardingToast';
import CompaniesStep from './steps/CompaniesStep';
import InvestingStyleStep from './steps/InvestingStyleStep';
import SignalsStep from './steps/SignalsStep';
// import PopularSearchesStep from './steps/PopularSearchesStep';
import { useOnboardingWizard } from './hooks/useOnboardingWizard';
import { useToast } from '../favorites-app/hooks/useToast';
import { COMPLETION_COPY, ONBOARDING_FLOW_HEADING, ONBOARDING_MODAL_SHELL_CLASS, STEP_COPY } from './constants';
import type { WizardStep } from './hooks/useOnboardingWizard';
import type { OnboardingPreferences } from './types';

function getOnboardingA11yLabels(step: WizardStep): { title: string; description: string } {
  if (step === 'completing') {
    return {
      title: COMPLETION_COPY.title,
      description: 'Building your screener and preparing results',
    };
  }

  const copyByStep = {
    1: STEP_COPY.companies,
    2: STEP_COPY.investingStyle,
    3: STEP_COPY.signals,
    // 4: STEP_COPY.popularSearches,
  } as const;

  const copy = copyByStep[step as keyof typeof copyByStep];
  if (!copy) {
    return { title: ONBOARDING_FLOW_HEADING, description: '' };
  }
  return { title: ONBOARDING_FLOW_HEADING, description: copy.subtext };
}

interface OnboardingModalProps {
  user: { id?: number };
  isOpen: boolean;
  initialStep?: number;
  initialPreferences?: OnboardingPreferences;
  onComplete: () => void;
  /** When true, shows the dialog close control (e.g. Customize Home edit flow). */
  showCloseButton?: boolean;
  onClose?: () => void;
}

export default function OnboardingModal({
  user,
  isOpen,
  initialStep = 1,
  initialPreferences = {},
  onComplete,
  showCloseButton = false,
  onClose,
}: OnboardingModalProps) {
  const wizard = useOnboardingWizard({
    userId: user?.id,
    initialStep,
    initialPreferences,
    onComplete,
    isPreferenceEditMode: showCloseButton,
  });
  const { showToast, toastMessage, showToastMessage } = useToast();

  const handleCompanyAdded = useCallback(
    (tickerSymbol: string) => {
      showToastMessage(STEP_COPY.companies.companyAddedToast(tickerSymbol));
    },
    [showToastMessage],
  );

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const { currentStep } = wizard;
  const isCompleting = currentStep === 'completing';
  const { title: dialogTitle, description: dialogDescription } =
    getOnboardingA11yLabels(currentStep);

  const stepContent = () => {
    if (isCompleting) return null;

    switch (currentStep) {
      case 1:
        return (
          <OnboardingStepFrame
            headline={STEP_COPY.companies.headline}
            subtext={STEP_COPY.companies.subtext}
            continueLabel={STEP_COPY.companies.continueCta}
            onContinue={wizard.goNext}
            isSubmitting={wizard.isSubmitting || wizard.isLoadingFavorites}
            error={wizard.error}
            continueAlign="end"
            scrollableContent={false}
            headerClassName="sm:mb-5"
            subtextClassName="sm:mt-2"
            contentClassName="sm:pt-4 sm:pb-2"
            errorClassName="mx-auto w-full max-w-md"
          >
            <CompaniesStep
              selected={wizard.selectedCompanies}
              isActive={isOpen && currentStep === 1}
              isLoadingFavorites={wizard.isLoadingFavorites}
              onSelect={wizard.addCompany}
              onRemove={wizard.removeCompany}
              onCompanyAdded={handleCompanyAdded}
            />
          </OnboardingStepFrame>
        );
      case 2:
        return (
          <OnboardingStepFrame
            headline={STEP_COPY.investingStyle.headline}
            subtext={STEP_COPY.investingStyle.subtext}
            continueLabel={STEP_COPY.investingStyle.continueCta}
            onContinue={wizard.goNext}
            onBack={wizard.goBack}
            canGoBack={wizard.canGoBack}
            isSubmitting={wizard.isSubmitting}
            error={wizard.error}
            contentClassName="sm:pt-5 sm:pb-2"
            errorClassName="mx-auto w-full max-w-md"
            scrollableContent={false}
          >
            <InvestingStyleStep
              selected={wizard.selectedInvestingStyles}
              isActive={isOpen && currentStep === 2}
              onToggle={wizard.toggleInvestingStyle}
            />
          </OnboardingStepFrame>
        );
      case 3:
        return (
          <OnboardingStepFrame
            headline={STEP_COPY.signals.headline}
            subtext={STEP_COPY.signals.subtext}
            continueLabel={STEP_COPY.popularSearches.continueCta}
            onContinue={wizard.goNext}
            onBack={wizard.goBack}
            canGoBack={wizard.canGoBack}
            isSubmitting={wizard.isSubmitting}
            error={wizard.error}
            scrollableContent={false}
          >
            <SignalsStep
              selected={wizard.selectedSignals}
              isActive={isOpen && currentStep === 3}
              onToggle={wizard.toggleSignal}
              onSelectAll={wizard.selectAllSignals}
              onClearAll={wizard.clearSelectedSignals}
            />
          </OnboardingStepFrame>
        );
      // case 4:
      //   return (
      //     <OnboardingStepFrame
      //       headline={STEP_COPY.popularSearches.headline}
      //       subtext={STEP_COPY.popularSearches.subtext}
      //       continueLabel={STEP_COPY.popularSearches.continueCta}
      //       onContinue={wizard.goNext}
      //       onBack={wizard.goBack}
      //       canGoBack={wizard.canGoBack}
      //       isSubmitting={wizard.isSubmitting}
      //       error={wizard.error}
      //       scrollableContent={false}
      //     >
      //       <PopularSearchesStep
      //         selected={wizard.selectedPopularSearches}
      //         isActive={isOpen && currentStep === 4}
      //         onToggle={wizard.togglePopularSearch}
      //         onSelectAll={wizard.selectAllPopularSearches}
      //         onClearAll={wizard.clearSelectedPopularSearches}
      //       />
      //     </OnboardingStepFrame>
      //   );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && showCloseButton) {
          handleClose();
        }
      }}
    >
      <DialogContent
        className={`max-w-[calc(100%-1rem)] sm:max-w-[50rem] [&>button]:hidden p-0 gap-0 outline-none focus:outline-none ${showCloseButton ? 'z-[60]' : ''} ${ONBOARDING_MODAL_SHELL_CLASS}`}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          if (!showCloseButton) {
            e.preventDefault();
          }
        }}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        {showCloseButton ? (
          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-end px-4 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="pointer-events-auto rounded-md bg-white text-gray-900 hover:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faCircleXmark} className="size-6" />
            </button>
          </div>
        ) : null}
        <DialogTitle className="sr-only">{dialogTitle}</DialogTitle>
        <DialogDescription className="sr-only">{dialogDescription}</DialogDescription>
        {!isCompleting && <OnboardingProgress currentStep={currentStep} />}

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <AnimatePresence mode="wait">
            {isCompleting ? (
              <motion.div
                key="completing"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex min-h-0 flex-1 flex-col"
              >
                <OnboardingCompletion />
              </motion.div>
            ) : (
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
                className="flex min-h-0 flex-1 flex-col"
              >
                {stepContent()}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <OnboardingToast message={toastMessage} isVisible={showToast && currentStep === 1} />
      </DialogContent>
    </Dialog>
  );
}
